import { useCallback, useMemo, useRef, useState } from 'react';
import type { FlatfinderPerspective } from 'realitiq-utils/src/flatfinder';

type History = {
  past: FlatfinderPerspective[][];
  present: FlatfinderPerspective[];
  future: FlatfinderPerspective[][];
};

export function useLayoutHistory(initial: FlatfinderPerspective[]) {
  const [history, setHistory] = useState<History>({ past: [], present: initial, future: [] });
  const serializedPresent = useMemo(() => JSON.stringify(history.present), [history.present]);
  const saved = useRef<string>();
  if (saved.current === undefined) saved.current = serializedPresent;
  const commit = useCallback(
    (update: (current: FlatfinderPerspective[]) => FlatfinderPerspective[]) => {
      setHistory((current) => {
        const next = update(current.present).map((perspective, sortOrder) => ({
          ...perspective,
          sortOrder,
          regions: perspective.regions.map((region, regionOrder) => ({
            ...region,
            sortOrder: regionOrder,
          })),
        }));
        if (JSON.stringify(next) === JSON.stringify(current.present)) return current;
        return { past: [...current.past.slice(-49), current.present], present: next, future: [] };
      });
    },
    []
  );
  const undo = useCallback(
    () =>
      setHistory((current) => {
        const previous = current.past.at(-1);
        return previous
          ? {
              past: current.past.slice(0, -1),
              present: previous,
              future: [current.present, ...current.future],
            }
          : current;
      }),
    []
  );
  const redo = useCallback(
    () =>
      setHistory((current) => {
        const next = current.future[0];
        return next
          ? {
              past: [...current.past, current.present],
              present: next,
              future: current.future.slice(1),
            }
          : current;
      }),
    []
  );
  const reset = useCallback((perspectives: FlatfinderPerspective[]) => {
    saved.current = JSON.stringify(perspectives);
    setHistory({ past: [], present: perspectives, future: [] });
  }, []);
  const dirty = saved.current !== serializedPresent;
  return {
    perspectives: history.present,
    commit,
    undo,
    redo,
    reset,
    dirty,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
  };
}
